/**
 * containerOps — shared predicates and transforms for container operations
 * (wrap, unwrap, edge-drag, convert).
 *
 * Runs in both the iframe (hydra.js) and the admin (volto-hydra). Pure JS;
 * no DOM, no Redux, no intl. Callers supply container configs already resolved
 * from the block registry (e.g. via getContainerFieldConfig).
 */

/**
 * Can a container accept one more block of a given type?
 *
 * @param {object} config         Container field config (allowedBlocks, maxLength, etc.)
 * @param {string} blockType      The block @type being considered
 * @param {number} currentCount   Current number of children in the container
 * @returns {boolean}
 */
export function canContain(config, blockType, currentCount) {
  if (config?.readOnly) return false;
  if (config?.fixed) return false;
  const { allowedBlocks, maxLength } = config || {};
  if (allowedBlocks != null && !allowedBlocks.includes(blockType)) return false;
  if (maxLength != null && currentCount >= maxLength) return false;
  return true;
}

// canContainAll moved to @volto-hydra/helpers so SSR template-merge code
// can call it without dragging in the iframe-only container ops here.
// containerOps.test.js imports it from @volto-hydra/helpers.

/**
 * Find the shortest conversion path from srcType to any allowedTarget, using
 * Volto's fieldMappings convention: blocksConfig[target].fieldMappings[source]
 * defines an edge source → target.
 *
 * Returns an array of types from srcType to the chosen target, or null if no
 * path exists within `depth` edges (default 3 — caller memoization is
 * recommended for hot paths like DropdownMenu rendering).
 *
 * @param {string} srcType
 * @param {string[]} allowedTargets
 * @param {object} blocksConfig
 * @param {number} [depth=3]     Max number of edges to traverse.
 * @returns {string[] | null}
 */
/**
 * Map a source container's children into the shape of a target container.
 * Supports the blocks_layout pattern (shared `blocks` dict + `{fieldName}.items`).
 *
 * Returns an object with `blocks` and `[targetConfig.fieldName]`, which the
 * caller can merge into a target block shell.
 *
 * object_list containers (items as inline arrays with @id/idField) are a
 * follow-up — not handled here.
 *
 * @param {{fieldName: string}} sourceConfig
 * @param {{fieldName: string}} targetConfig
 * @param {object} sourceBlock
 * @returns {{blocks: object, [fieldName: string]: {items: string[]}}}
 */
export function mapLayoutItems(sourceConfig, targetConfig, sourceBlock) {
  const sourceField = sourceConfig.fieldName;
  const targetField = targetConfig.fieldName;
  const sourceLayout = sourceBlock[sourceField];
  const blocks = sourceBlock.blocks ?? {};

  // Carry every region (the default `items` plus any named regions) into the
  // target field. Non-array sub-keys are not regions and are ignored.
  const regions = {};
  if (sourceLayout && typeof sourceLayout === 'object') {
    for (const [region, ids] of Object.entries(sourceLayout)) {
      if (Array.isArray(ids)) regions[region] = ids;
    }
  }
  if (!regions.items) regions.items = [];

  return {
    blocks,
    [targetField]: regions,
  };
}

/**
 * Types allowed for blocks EXPELLED from a container (edge-drag expel).
 *
 * Expelled children become siblings of the container, so they must satisfy the
 * container's OWN region constraints — which buildBlockPathMap already resolved
 * per region into `allowedSiblingTypes`. The raw field-level `allowedBlocks` of
 * the parent's container field is only a fallback for the rare case where no
 * region-resolved types are available; using it directly would ignore a
 * region's narrower allowedBlocks.
 *
 * @param {Object|null} containerInfo - The container's blockPathMap entry
 * @param {Object|null} [parentFieldDef] - The parent's container field schema (fallback)
 * @returns {string[]|null}
 */
export function expelAllowedTypes(containerInfo, parentFieldDef = null) {
  if (containerInfo?.allowedSiblingTypes != null) {
    return containerInfo.allowedSiblingTypes;
  }
  return parentFieldDef?.allowedBlocks ?? null;
}

export function findConversionPath(
  srcType,
  allowedTargets,
  blocksConfig,
  depth = 3,
) {
  if (!srcType || !blocksConfig?.[srcType]) return null;
  const targetSet = new Set(allowedTargets);
  if (targetSet.has(srcType)) return [srcType];

  const parents = new Map();
  parents.set(srcType, null);
  let frontier = [srcType];
  for (let hop = 1; hop <= depth; hop++) {
    const next = [];
    for (const current of frontier) {
      for (const [candidate, candidateCfg] of Object.entries(blocksConfig)) {
        if (parents.has(candidate)) continue;
        if (!candidateCfg?.fieldMappings) continue;
        if (!candidateCfg.fieldMappings[current]) continue;
        parents.set(candidate, current);
        if (targetSet.has(candidate)) {
          const path = [candidate];
          let node = current;
          while (node !== null) {
            path.unshift(node);
            node = parents.get(node);
          }
          return path;
        }
        next.push(candidate);
      }
    }
    if (next.length === 0) break;
    frontier = next;
  }
  return null;
}

/**
 * The only child of a container that is a synthetic 'empty' placeholder — or null. Region-aware
 * via blockPathMap (which records parentId for EVERY child, blocks_layout AND object_list), so it
 * also spots an empty object_list container (a slider seeded with one placeholder slide). The drag
 * "drop into an empty container's whitespace" gesture uses this; the earlier `blocks_layout.items`
 * read missed object_list containers entirely.
 *
 * @param {Object} blockPathMap - uid -> { parentId, blockType, ... }
 * @param {string} containerUid - the drop-target container's uid
 * @returns {string|null} the placeholder child's uid, or null
 */
export function findOnlyEmptyChildUid(blockPathMap, containerUid) {
  if (!blockPathMap) return null;
  const childUids = Object.keys(blockPathMap).filter(
    (uid) => blockPathMap[uid]?.parentId === containerUid,
  );
  return childUids.length === 1 &&
    blockPathMap[childUids[0]]?.blockType === 'empty'
    ? childUids[0]
    : null;
}
